import './App.css';
import { useEffect, useRef, useState } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import HeaderBar from './layout/HeaderBar';
import NavBar from './layout/NavBar';
import ClientHistoryPage from './pages/ClientHistoryPage';
import PredictedDiagnosesPage from './pages/PredictedDiagnosesPage/PredictedDiagnosesPage';
import ConfirmationTestsPage from './pages/ConfirmationTestsPage/ConfirmationTestsPage';
import DsmAssociationsPage from './pages/DsmAssociations/DsmAssociationsPage';
import DsmReferencePage from './pages/DsmReferencePage/DsmReferencePage';
import Page from './layout/Page';
import { routes } from './constants';
import { components } from './api/types';
import {
  onStart,
  onCorrelationsLoad,
  onUserIdChange,
  onOneFilterChange,
  onTwoFilterChange,
} from './api/facade';


type FilterParams = components["schemas"]["FilterParams"];

const App = () => {

  /* Loading states */
  const [onStartLoading, setOnStartLoading] = useState<boolean>(true);
  const [onUserIdChangeLoading, setOnUserIdChangeLoading] = useState<boolean>(true);
  const [onCorrelationsLoading, setOnCorrelationsLoading] = useState<boolean>(true);
  const [onOneFilterChangeLoading, setOnOneFilterChangeLoading] = useState<boolean>(true);
  const [onTwoFilterChangeLoading, setOnTwoFilterChangeLoading] = useState<boolean>(true);

  /* One-time variables */
  const [availableIds, setAvailableIds] = useState<Record<string, string>>({});
  const [allDiagnosisNames, setAllDiagnosisNames] = useState<string[]>([]);
  const [allMeasureNames, setAllMeasureNames] = useState<string[]>([]);

  /* True diagnosis vs false diagnosis.
   * Diagnosis-measure correlations for one diagnosis.
   * In form number[<diagnosis>][<measure>] */
  const [diagnosisMeasureCorrelationPercents, setDiagnosisMeasureCorrelationPercents] = useState<number[][]>([]);

  /* User change variables */
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [actualAge, setActualAge] = useState<number | null>(null);
  const [actualSex, setActualSex] = useState<string>('');
  const [actualRace, setActualRace] = useState<string | null>(null);
  const [actualEthnicity, setActualEthnicity] = useState<string | null>(null);
  const [actualScores, setActualScores] = useState<(number | null)[]>([]);
  const [actualDiagnosisMask, setActualDiagnosisMask] = useState<boolean[]>([]);
  const [takenMeasureMask, setTakenMeasureMask] = useState<boolean[]>([]);
  const [actualPercentileRanks, setActualPercentileRanks] = useState<(number | null)[]>([]);
  const [predictedPercentileRanks, setPredictedPercentileRanks] = useState<(number | null)[]>([]);
  const [posteriors, setPosteriors] = useState<number[]>([])
  const [predictedMeans, setPredictedMeans] = useState<number[]>([]);
  const [predictedStds, setPredictedStds] = useState<number[]>([]);
  const [allDiagnosisLabels, setAllDiagnosisLabels] = useState<(string | null)[]>([]);
  const [dsmAssociations, setDsmAssociations] = useState<components["schemas"]["DsmEntry"][]>([]);

  /* One filter change variables */
  const [priors, setPriors] = useState<(number | null)[]>([]);
  const [predictedDiagnosisQuantiles, setPredictedDiagnosisQuantiles] = useState<(components["schemas"]["Quantiles"] | null)[]>([]);

  /* Two filter change variables */
  const [twoDiagnosesCorrelatedMeasures, setTwoDiagnosesCorrelatedMeasures] = useState<number[]>([]);
  const [confirmationTestsQuantiles1, setConfirmationTestsQuantiles1] = useState<(components["schemas"]["Quantiles"] | null)[]>([]);
  const [confirmationTestsQuantiles2, setConfirmationTestsQuantiles2] = useState<(components["schemas"]["Quantiles"] | null)[]>([]);

  /* Cached filters */
  const [predictedDiagnosisFilter, setPredictedDiagnosisFilter] = useState<FilterParams>({});
  const [confirmationTestsFilter1, setConfirmationTestsFilter1] = useState<FilterParams>({});
  const [confirmationTestsFilter2, setConfirmationTestsFilter2] = useState<FilterParams>({});

  /* Initial fetch */
  useEffect(() => {
    setOnStartLoading(true);
    onStart()
      .then((bundle) => {
        setSelectedId(bundle.selectedIdentifier);
        setAvailableIds(bundle.availableIdentifiers);
        setAllDiagnosisNames(bundle.allDiagnosisNames);
        setAllMeasureNames(bundle.allMeasureNames);
      })
      .finally(() => setOnStartLoading(false));
  }, []);

  /* Diagnosis-measure correlations: patient-independent, so loaded once, after the first user bundle.
     The backend handles requests one at a time, so loading these any earlier delays the patient's data. */
  const correlationsRequested = useRef(false);
  useEffect(() => {
    if (correlationsRequested.current || !allDiagnosisNames.length || onUserIdChangeLoading) return;
    correlationsRequested.current = true;
    onCorrelationsLoad(allDiagnosisNames)
      .then((correlationPercents) => setDiagnosisMeasureCorrelationPercents(correlationPercents))
      .finally(() => setOnCorrelationsLoading(false));
  }, [allDiagnosisNames, onUserIdChangeLoading]);

  /* On User Id Change */
  useEffect(() => {
    if (!selectedId) return;
    setOnUserIdChangeLoading(true);
    onUserIdChange(selectedId)
      .then((bundle) => {
        setActualAge(bundle.actualAge);
        setActualSex(bundle.actualSex);
        setActualRace(bundle.actualRace);
        setActualEthnicity(bundle.actualEthnicity);
        setActualDiagnosisMask(bundle.actualDiagnosisMask);
        setTakenMeasureMask(bundle.takenMeasureMask);
        setActualScores(bundle.actualScores);
        setActualPercentileRanks(bundle.actualPercentileRanks);
        setPredictedPercentileRanks(bundle.predictedPercentileRanks);
        setPosteriors(bundle.posteriors);
        setPredictedMeans(bundle.predictedMeans);
        setPredictedStds(bundle.predictedStds);
        setAllDiagnosisLabels(bundle.allDiagnosisLabels);
        setDsmAssociations(bundle.dsmAssociations);

        // setDiagnosisColors(bundle.allDiagnosisLabels)
      })
      .finally(() => setOnUserIdChangeLoading(false));
  }, [selectedId]);

  /* One filter change */
  useEffect(() => {
    if (!predictedDiagnosisFilter) return;
    setOnOneFilterChangeLoading(true);
    onOneFilterChange(predictedDiagnosisFilter)
      .then((bundle) => {
        setPriors(bundle.priors);
        setPredictedDiagnosisQuantiles(bundle.quantiles);
      })
      .finally(() => setOnOneFilterChangeLoading(false));
  }, [predictedDiagnosisFilter]);

  /* Two filter change */
  useEffect(() => {
    if (!confirmationTestsFilter1 || !confirmationTestsFilter2) return;
    setOnTwoFilterChangeLoading(true);
    onTwoFilterChange(confirmationTestsFilter1, confirmationTestsFilter2)
      .then((bundle) => {
        setTwoDiagnosesCorrelatedMeasures(bundle.diagnosesCorrelatedMeasures);
        setConfirmationTestsQuantiles1(bundle.quantiles1);
        setConfirmationTestsQuantiles2(bundle.quantiles2);
      })
      .finally(() => setOnTwoFilterChangeLoading(false));
  }, [confirmationTestsFilter1, confirmationTestsFilter2]);

















    


  // /* One-time variables */
  // const [availableIds, setAvailableIds] = useState<string[]>([]);
  // const [availableIdDescriptions, setAvailableIdDescriptions] = useState<string[]>([]);
  // const [allDiagnosisNames, setAllDiagnosisNames] = useState<string[]>([]);

  // /* User change variables */
  // const [selectedId, setSelectedId] = useState<string>('');
  // const [actualAge, setActualAge] = useState<number | null>(null);
  // const [actualSex, setActualSex] = useState<string>('');
  // const [actualRace, setActualRace] = useState<string>('');
  // const [actualEthnicity, setActualEthnicity] = useState<string>('');
  // const [actualDiagnosisNames, setActualDiagnosisNames] = useState<string[]>([]);
  // const [posteriors, setPosteriors] = useState<number[]>([])
  // const [takenMeasures, setTakenMeasures] = useState<Measure[]>([]);
  // const [notTakenMeasures, setNotTakenMeasures] = useState<Measure[]>([]);
  // const [predictedMeans, setPredictedMeans] = useState<number[]>([]);
  // const [predictedStds, setPredictedStds] = useState<number[]>([]);
  // const [allDiagnosisLabels, setAllDiagnosisLabels] = useState<string[]>([]);
  // const [dsmAssociations, setDsmAssociations] = useState<components["schemas"]["DsmEntry"][]>([]);

  // /* One filter change variables */
  // const [priors, setPriors] = useState<number[] | null>(null);
  // const [populationMeans, setPopulationMeans] = useState<(number | null)[]>([]);
  // const [populationStds, setPopulationStds] = useState<(number | null)[]>([]);
  // const [quantiles, setQuantiles] = useState<(components["schemas"]["Quantiles"] | null)[]>([]);

  // /* Two filter change variables */
  // const [diagnosesCorrelatedMeasures, setDiagnosesCorrelatedMeasures] = useState<Measure[]>([]);

  // type ClientData = {
  //   id: string;
  //   age: number | null;
  //   sex: string;
  //   race: string;
  //   diagnoses: Diagnosis[];
  //   measures: Measure[];
  //   measuresNotTaken: Measure[];
  // };

  // type DiagnosesData = {


  //   actualDiagnoses: Diagnosis[];
  //   allDiagnosisNames: string[];
  //   consensusDiagnoses: Diagnosis[];
  //   nonConsensusDiagnoses: Diagnosis[];
  //   newDiagnoses: Diagnosis[];
  //   diagnosesStats: Diagnoses;
  // };













  




  // const [ids, setIds] = useState<string[]>([]);
  // const [idNames, setIdNames] = useState<string[]>([]);
  // const [selectedId, setSelectedId] = useState('');
  // const [loading, setLoading] = useState(false);
  // const [clientData, setClientData] = useState<ClientData>({
  //   id: '',
  //   age: null,
  //   sex: '',
  //   race: '',
  //   diagnoses: [],
  //   measures: [],
  //   measuresNotTaken: [],
  //   // diagnosisCorrelatedMeasures: [],
  //   loading: true,
  // });
  // const [diagnosesData, setDiagnosesData] = useState<DiagnosesData>({
  //   actualDiagnoses: [],
  //   allDiagnosisNames: [],
  //   consensusDiagnoses: [],
  //   nonConsensusDiagnoses: [],
  //   newDiagnoses: [],
  //   diagnosesStats: {},
  //   loading: true,
  // });

  // const updateLoadingStatus = (status: boolean) => {
  //   setLoading(status);
  // }

  // const updateSelectedId = (id: string) => {
  //   setSelectedId(id);
  // };



  // useEffect(() => {
  //   const fetchSelectedId = async () => {
  //     const response = await getSelectedIdentifier();
  //     setSelectedId(response);
  //   };
  //   const fetchIds = async () => {
  //     const response = await getAvailableIdentifiers();
  //     setIds(Object.keys(response));
  //     setIdNames(Object.values(response));
  //   };
  //   fetchIds();
  //   fetchSelectedId();
  // }, []);

  // useEffect(() => {
  //   const fetchClientAndDiagnosisData = async () => {
  //     const [
  //       ageRes,
  //       sexRes,
  //       raceRes,
  //       actualDiagnosesRes,
  //       measuresTakenRes,
  //       measuresNotTakenRes,
  //       predictedMeansRes,
  //       predictedStdsRes,
  //       allDiagnosisNamesRes,
  //     ] = await Promise.all([
  //       getActualAge(),
  //       getActualSex(),
  //       getActualRace(),
  //       getActualDiagnosisNames(),
  //       getTakenMeasures(),
  //       getNotTakenMeasures(),
  //       getPredictedMeans(),
  //       getPredictedStds(),
  //       getAllDiagnosisNames(),
  //     ]);

  //     const predictedScores = predictedMeansRes.map(({ measureName, mean, percentile }) => {
  //       return { measureName: measureName, mean: mean, percentile: percentile, std: predictedStdsRes.find((res) => res.measureName === measureName)?.std };
  //     });

  //     const measuresNotTaken = measuresNotTakenRes.map((measure: Measure) => {
  //       const predictedScore = predictedScores.find(({ measureName }) => measureName === measure.measureName);
  //       if (predictedScore) {
  //         measure.predictedScore = predictedScore.mean;
  //         measure.percentile = (predictedScore.percentile && roundToDecimal(predictedScore.percentile, 2)) || null;
  //         measure.predictedStd = predictedScore.std;
  //       }
  //       return measure;
  //     });

  //     const measuresTaken = measuresTakenRes.map((measure: Measure) => {
  //       const predictedScore = predictedScores.find(({ measureName }) => measureName === measure.measureName);
  //       if (predictedScore) {
  //         measure.predictedScore = predictedScore.mean;
  //         measure.predictedStd = predictedScore.std;
  //       }
  //       measure.percentile = (measure.percentile && roundToDecimal(measure.percentile * 100, 2)) || null;
  //       return measure;
  //     });

  //     setClientData({
  //       id: selectedId,
  //       age: ageRes,
  //       sex: sexRes,
  //       race: raceRes,
  //       diagnoses: actualDiagnosesRes,
  //       measures: measuresTaken,
  //       measuresNotTaken: measuresNotTaken,
  //       loading: loading,
  //     });

  //     const diagnosesStats: Diagnoses = {};
  //     const statsPromises = allDiagnosisNamesRes.map(async (diagnosis: string) => {
  //       const [prior, posterior, correlatedMeasures] = await Promise.all([
  //         getPrior({ "diagnosis": diagnosis, "filterParams": {}}),
  //         getPosterior({ "diagnosis": diagnosis }),
  //         getDiagnosisCorrelatedMeasures({ "diagnosis": diagnosis }),
  //       ]);
  //       const updatedPercentileMeasures = correlatedMeasures.map((measure: Measure) => {
  //         measure.percentile = (measure.percentile && roundToDecimal(measure.percentile * 100, 2)) || null;
  //         return measure;
  //       });
  //       const stats: DiagnosisStats = {
  //         prior: prior,
  //         posterior: posterior,
  //         correlatedMeasures: updatedPercentileMeasures,
  //       };
  //       diagnosesStats[diagnosis] = stats;
  //     });
  //     await Promise.all(statsPromises);

  //     // Helper: safe odds ratio; returns null if prior is null/0 or missing
  //     const getOddsRatio = (d: string): number | null => {
  //       const pr = diagnosesStats[d]?.prior;
  //       const po = diagnosesStats[d]?.posterior;
  //       if (pr === null || pr === 0 || pr === undefined || po === undefined) return null;
  //       return po / pr;
  //     };

  //     /* Diagnoses on which clinician and InsightCare agree */
  //     const consensusDiagnoses = actualDiagnosesRes.filter(({ diagnosis }) => {
  //       const o = getOddsRatio(diagnosis);
  //       return allDiagnosisNamesRes.find((d: string) => diagnosis === d) && o !== null && o > 1;
  //     });

  //     /* Diagnoses on which clinician and InsightCare disagree */
  //     const nonConsensusDiagnoses = actualDiagnosesRes.filter(({ diagnosis }) => {
  //       const o = getOddsRatio(diagnosis);
  //       return allDiagnosisNamesRes.find((d: string) => diagnosis === d) && o !== null && o <= 1;
  //     });

  //     /* Diagnoses which InsightCare proposes which were not identified by the clinician */
  //     const newDiagnosisNames = allDiagnosisNamesRes.filter((d: string) => {
  //       const o = getOddsRatio(d);
  //       return !actualDiagnosesRes.find(({ diagnosis }) => diagnosis === d) && o !== null && o > 1;
  //     });

  //     const newDiagnoses = newDiagnosisNames.map((diagnosis: string) => ({ diagnosis: diagnosis, label: "" }));

  //     setDiagnosesData({
  //       actualDiagnoses: actualDiagnosesRes,
  //       allDiagnosisNames: allDiagnosisNamesRes,
  //       consensusDiagnoses: consensusDiagnoses,
  //       nonConsensusDiagnoses: nonConsensusDiagnoses,
  //       newDiagnoses: newDiagnoses,
  //       diagnosesStats: diagnosesStats,
  //       loading: loading,
  //     });
  //   };
  //   if (selectedId && !loading) {
  //     fetchClientAndDiagnosisData();
  //   }
  // }, [selectedId]);




















  const oddsRatios = priors.map((_, i: number) => {
    const prior = priors[i];
    const posterior = posteriors[i];
    if (prior === null || prior === 0) {
      return 0;
    }
    return posterior / prior;
  });

  const agreedDiagnosisMask = actualDiagnosisMask.map((_, i: number) => {
    const actualTruePredTrue = actualDiagnosisMask[i] && oddsRatios[i] > 1;
    // const actualFalsePredFalse = !actualDiagnosisMask[i] && oddsRatios[i] <= 1;
    return actualTruePredTrue;// || actualFalsePredFalse;
  });

  const disagreedDiagnosisMask = actualDiagnosisMask.map((_, i: number) => {
    const actualTruePredFalse = actualDiagnosisMask[i] && oddsRatios[i] <= 1;
    return actualTruePredFalse;
  });

  const recommendedDiagnosisMask = actualDiagnosisMask.map((_, i: number) => {
    const actualFalsePredTrue = !actualDiagnosisMask[i] && oddsRatios[i] > 1;
    return actualFalsePredTrue;
  });

  const diagnosisColors = allDiagnosisNames.map((_, i: number) => {
    if (agreedDiagnosisMask[i]) {
      return "#18A103";
    } else if (disagreedDiagnosisMask[i]) {
      return "#FB5607";
    } else if (recommendedDiagnosisMask[i]) {
      return "#FFBE0B";
    }
    // return "#eef2f8";
    return "#aaaaaa"
  });

  const headerBar: JSX.Element = (
    <HeaderBar
      availableIds={availableIds}
      selectedId={selectedId}
      setSelectedId={setSelectedId}
      loading={onStartLoading}
    />
  );

  return (
    <BrowserRouter>
      <Page headerBar={headerBar} navBar={<NavBar />}>
        <Routes>
          <Route index path={routes.ClientHistory.path()} element={
            <ClientHistoryPage
              selectedId={selectedId}
              age={actualAge}
              sex={actualSex}
              race={actualRace}

              /* Diagnosis data */
              diagnosisNames={allDiagnosisNames.filter((_, i: number) => actualDiagnosisMask[i])}
              diagnosisLabels={allDiagnosisLabels.filter((_, i: number) => actualDiagnosisMask[i])}

              /* Measure data */
              measureNames={allMeasureNames.filter((_, i: number) => takenMeasureMask[i])}
              actualScores={actualScores.filter((_, i: number) => takenMeasureMask[i]).filter((x): x is number => x !== null)}
              actualPercentileRanks={actualPercentileRanks.filter((_, i: number) => takenMeasureMask[i]).filter((x): x is number => x !== null)}
              loading={onStartLoading || onUserIdChangeLoading}
            />}
          />
          <Route path={routes.PredictedDiagnoses.path()} element={
            <PredictedDiagnosesPage
              selectedId={selectedId}
              loading={onStartLoading || onUserIdChangeLoading || onOneFilterChangeLoading || onCorrelationsLoading}

              /* Client data */
              age={actualAge}
              sex={actualSex}
              race={actualRace}
              ethnicity={actualEthnicity}
            
              /* Diagnosis data */
              diagnosisNames={allDiagnosisNames}
              diagnosisLabels={allDiagnosisLabels}
              oddsRatios={oddsRatios}
              actualDiagnosisMask={actualDiagnosisMask}
              agreedDiagnosisMask={agreedDiagnosisMask}
              disagreedDiagnosisMask={disagreedDiagnosisMask}
              recommendedDiagnosisMask={recommendedDiagnosisMask}
            
              /* Measure data */
              measureNames={allMeasureNames}
              actualScores={actualScores}
              actualPercentileRanks={actualPercentileRanks}
              correlationPercents={diagnosisMeasureCorrelationPercents}  // TODO: Pre-load correlations as number[<diagnosisNames>][<measureNames>]
              takenMeasureMask={takenMeasureMask}
              quantiles={predictedDiagnosisQuantiles}
              
              /* Filter data */
              setFilter={setPredictedDiagnosisFilter}
            />}
          />
          <Route path={routes.ConfirmationTests.path()} element={
            <ConfirmationTestsPage
              /* Measure data */
              measureNames={allMeasureNames.filter((_, i: number) => !takenMeasureMask[i])}
              measureCorrelationPercents={twoDiagnosesCorrelatedMeasures.filter((_, i: number) => !takenMeasureMask[i])}
              predictedScores={predictedMeans.filter((_, i: number) => !takenMeasureMask[i])}
              predictedStds={predictedStds.filter((_, i: number) => !takenMeasureMask[i])}
              predictedPercentileRanks={predictedPercentileRanks.filter((_, i: number) => !takenMeasureMask[i])}
            
              /* Diagnosis data */
              diagnosisNames={allDiagnosisNames}
              actualDiagnosisMask={actualDiagnosisMask}
              diagnosisColors={diagnosisColors}
              quantiles1={confirmationTestsQuantiles1}
              quantiles2={confirmationTestsQuantiles2}
              setFilterParams1={setConfirmationTestsFilter1}
              setFilterParams2={setConfirmationTestsFilter2}

              /* Other data */
              loading={onStartLoading || onUserIdChangeLoading || onTwoFilterChangeLoading}
            />}
          />
          <Route path={routes.DsmAssociations.path()} element={
            <DsmAssociationsPage
              selectedId={selectedId}
              dsmAssociations={dsmAssociations}
              measureNames={allMeasureNames}
              takenMeasureMask={takenMeasureMask}
              loading={onUserIdChangeLoading}
            />}
          />
          <Route path={routes.DsmReference.path()} element={<DsmReferencePage />} />
        </Routes>
      </Page>
    </BrowserRouter>
  );
};

export default App;